// src/context/ProfilePrefetchContext.jsx
/**
 * ProfilePrefetchContext
 * ----------------------
 * Global, session-scoped cache for user "profile/demographics" data.
 *
 * What is cached
 *  - Profile JSON: most recent metadata JSON object for a uid.
 *  - Profile info: newest metadata timestamp (and optionally filename) for a uid
 *    without downloading the JSON payload (fast gate checks).
 *
 * Goals
 *  - Prefetch on login (non-blocking) to reduce later in-flow latency.
 *  - Avoid redundant blocking fetches in assessment/questionnaire flows.
 *  - Provide a stable API for:
 *      • getCachedProfile / ensureProfile
 *      • getCachedProfileInfo / ensureProfileInfo
 *      • setProfile (updates caches after a save/confirm)
 *      • clearProfile / clearAllProfiles (hygiene on logout / uid change)
 *
 * Caching strategy
 *  - In-memory cache for fastest reads.
 *  - sessionStorage mirror to persist across reloads within the same tab.
 *  - TTL-based freshness checks.
 *
 * Notes
 *  - User/profile reads now flow through the protected regional backend rather
 *    than direct browser Firebase SDK access.
 *  - The backend may satisfy those reads from its owned user-context store or
 *    from controlled transitional fallbacks while migration is in progress.
 *  - This context is still an optimisation layer to reduce UX latency in
 *    "review/prefill" and gating flows.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { fetchLatestMetadata } from "../utils/firebase/fetchLatestMetadata";
import { fetchLatestMetadataInfo } from "../utils/firebase/fetchLatestMetadataInfo";

/** Session storage keys (exported for reuse/debugging). */
export const SESSION_KEY_PREFIX = "pdx.profilePrefetch.v1";
export const SESSION_INFO_KEY_PREFIX = "pdx.profilePrefetch.info.v1";

/**
 * Default TTL (10 minutes) for cache freshness checks.
 * Exported so callers can standardise behaviour.
 */
export const DEFAULT_TTL_MS = 10 * 60 * 1000;

/**
 * Short retry backoff used for failed backend profile lookups.
 *
 * This prevents aggressive request loops when the backend is unhealthy or
 * temporarily misconfigured, while still letting the app recover quickly after
 * an external fix such as an IAM update.
 */
export const ERROR_RETRY_TTL_MS = 30 * 1000;

/**
 * @typedef {object} CachedProfile
 * @property {"idle"|"loading"|"ready"|"error"} status
 * @property {object|null} data
 * @property {string|null} error
 * @property {number|null} fetchedAtMs
 */

/**
 * @typedef {object} CachedProfileInfo
 * @property {"idle"|"loading"|"ready"|"error"} status
 * @property {number|null} latestMs
 * @property {string|null} newestName
 * @property {string|null} error
 * @property {number|null} fetchedAtMs
 */

const ProfilePrefetchContext = createContext(null);

/**
 * Returns true if a cached entry is within TTL.
 *
 * @param {{ fetchedAtMs?: number|null } | null | undefined} entry
 * @param {number} ttlMs
 * @returns {boolean}
 */
export function isEntryFresh(entry, ttlMs) {
  if (!entry?.fetchedAtMs) return false;
  return Date.now() - entry.fetchedAtMs <= ttlMs;
}

/**
 * React provider that supplies profile cache + fetch helpers.
 *
 * @param {{ children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function ProfilePrefetchProvider({ children }) {
  /** @type {[Record<string, CachedProfile>, Function]} */
  const [cacheByUid, setCacheByUid] = useState({});
  /** @type {[Record<string, CachedProfileInfo>, Function]} */
  const [infoByUid, setInfoByUid] = useState({});

  // Track in-flight requests (dedupe concurrent callers)
  const inflightProfileRef = useRef({});
  const inflightInfoRef = useRef({});

  /* ------------------------------------------------------------------ */
  /* sessionStorage helpers                                              */
  /* ------------------------------------------------------------------ */

  const readSession = useCallback((uid) => {
    try {
      const raw = sessionStorage.getItem(`${SESSION_KEY_PREFIX}:${uid}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const writeSession = useCallback((uid, value) => {
    try {
      sessionStorage.setItem(
        `${SESSION_KEY_PREFIX}:${uid}`,
        JSON.stringify(value),
      );
    } catch {
      // ignore quota / disabled storage
    }
  }, []);

  const readSessionInfo = useCallback((uid) => {
    try {
      const raw = sessionStorage.getItem(`${SESSION_INFO_KEY_PREFIX}:${uid}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, []);

  const writeSessionInfo = useCallback((uid, value) => {
    try {
      sessionStorage.setItem(
        `${SESSION_INFO_KEY_PREFIX}:${uid}`,
        JSON.stringify(value),
      );
    } catch {
      // ignore quota / disabled storage
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /* getters                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Returns best available cached profile entry for uid (memory first, then sessionStorage).
   *
   * @param {string} uid
   * @returns {CachedProfile|null}
   */
  const getCachedProfile = useCallback(
    (uid) => {
      if (!uid) return null;
      if (cacheByUid[uid]) return cacheByUid[uid];
      return readSession(uid);
    },
    [cacheByUid, readSession],
  );

  /**
   * Returns best available cached profile info entry for uid (memory first, then sessionStorage).
   *
   * @param {string} uid
   * @returns {CachedProfileInfo|null}
   */
  const getCachedProfileInfo = useCallback(
    (uid) => {
      if (!uid) return null;
      if (infoByUid[uid]) return infoByUid[uid];
      return readSessionInfo(uid);
    },
    [infoByUid, readSessionInfo],
  );

  /* ------------------------------------------------------------------ */
  /* mutation / hygiene                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Writes profile JSON into caches (memory + sessionStorage).
   * Also updates "info" cache with a new "latestMs" (best-effort) because a save/confirm
   * implies a new artefact exists.
   *
   * @param {string} uid
   * @param {object|null} data
   */
  const setProfile = useCallback(
    (uid, data) => {
      if (!uid) return;

      const now = Date.now();

      /** @type {CachedProfile} */
      const entry = {
        status: "ready",
        data: data ?? null,
        error: null,
        fetchedAtMs: now,
      };

      setCacheByUid((prev) => ({ ...prev, [uid]: entry }));
      writeSession(uid, entry);

      /** @type {CachedProfileInfo} */
      const infoEntry = {
        status: "ready",
        latestMs: now,
        newestName: null,
        error: null,
        fetchedAtMs: now,
      };

      setInfoByUid((prev) => ({ ...prev, [uid]: infoEntry }));
      writeSessionInfo(uid, infoEntry);
    },
    [writeSession, writeSessionInfo],
  );

  /**
   * Writes a server-reported profile timestamp into the "info" caches without
   * touching the cached profile JSON.
   *
   * Used by the recertification confirmation, where the demographics are
   * deliberately unchanged but the profile timestamp has moved on. Passing the
   * server's own `latestMs` keeps the client gate aligned with the stored
   * record rather than guessing from the local clock.
   *
   * @param {string} uid
   * @param {{ latestMs?: number|null, newestName?: string|null }} info
   */
  const setProfileInfo = useCallback(
    (uid, info) => {
      if (!uid) return;

      const now = Date.now();

      /** @type {CachedProfileInfo} */
      const infoEntry = {
        status: "ready",
        latestMs: Number.isFinite(info?.latestMs) ? info.latestMs : null,
        newestName: info?.newestName ?? null,
        error: null,
        fetchedAtMs: now,
      };

      setInfoByUid((prev) => ({ ...prev, [uid]: infoEntry }));
      writeSessionInfo(uid, infoEntry);
    },
    [writeSessionInfo],
  );

  /**
   * Removes cached profile + info for a single uid (memory + sessionStorage).
   *
   * @param {string} uid
   */
  const clearProfile = useCallback((uid) => {
    if (!uid) return;

    setCacheByUid((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });

    setInfoByUid((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });

    try {
      sessionStorage.removeItem(`${SESSION_KEY_PREFIX}:${uid}`);
      sessionStorage.removeItem(`${SESSION_INFO_KEY_PREFIX}:${uid}`);
    } catch {
      // ignore
    }
  }, []);

  /**
   * Clears ALL cached profile + info entries (memory + sessionStorage).
   * Use on logout, or when the active uid changes.
   */
  const clearAllProfiles = useCallback(() => {
    setCacheByUid({});
    setInfoByUid({});

    try {
      const prefixA = `${SESSION_KEY_PREFIX}:`;
      const prefixB = `${SESSION_INFO_KEY_PREFIX}:`;

      for (let i = sessionStorage.length - 1; i >= 0; i -= 1) {
        const key = sessionStorage.key(i);
        if (!key) continue;
        if (key.startsWith(prefixA) || key.startsWith(prefixB)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      // ignore
    }
  }, []);

  /* ------------------------------------------------------------------ */
  /* ensure (fetch + dedupe)                                             */
  /* ------------------------------------------------------------------ */

  /**
   * Ensures profile JSON is available.
   *
   * Behaviour
   *  - If cached and fresh (TTL) and not forced, returns cached data immediately.
   *  - Otherwise fetches the latest metadata JSON from the regional backend
   *    (deduped per uid),
   *    updates caches, and returns the object (or null).
   *
   * @param {{ uid: string, ttlMs?: number, force?: boolean }} args
   * @returns {Promise<object|null>}
   */
  const ensureProfile = useCallback(
    async ({ uid, ttlMs = DEFAULT_TTL_MS, force = false }) => {
      if (!uid) throw new Error("ensureProfile: uid is required");

      const existing = getCachedProfile(uid);
      if (
        !force &&
        ((existing?.status === "ready" && isEntryFresh(existing, ttlMs)) ||
          (existing?.status === "error" &&
            isEntryFresh(existing, ERROR_RETRY_TTL_MS)))
      ) {
        return existing.data ?? null;
      }

      if (inflightProfileRef.current[uid])
        return inflightProfileRef.current[uid];

      // best-effort loading marker
      setCacheByUid((prev) => ({
        ...prev,
        [uid]: {
          status: "loading",
          data: prev[uid]?.data ?? null,
          error: null,
          fetchedAtMs: prev[uid]?.fetchedAtMs ?? null,
        },
      }));

      inflightProfileRef.current[uid] = (async () => {
        try {
          const data = await fetchLatestMetadata(uid); // object|null

          /** @type {CachedProfile} */
          const entry = {
            status: "ready",
            data: data ?? null,
            error: null,
            fetchedAtMs: Date.now(),
          };

          setCacheByUid((prev) => ({ ...prev, [uid]: entry }));
          writeSession(uid, entry);
          return data ?? null;
        } catch (e) {
          const msg = e?.message ? String(e.message) : "Unknown error";

          /** @type {CachedProfile} */
          const entry = {
            status: "error",
            data: null,
            error: msg,
            fetchedAtMs: Date.now(),
          };

          setCacheByUid((prev) => ({ ...prev, [uid]: entry }));
          writeSession(uid, entry);
          return null;
        } finally {
          delete inflightProfileRef.current[uid];
        }
      })();

      return inflightProfileRef.current[uid];
    },
    [getCachedProfile, writeSession],
  );

  /**
   * Ensures profile "info" (latest metadata timestamp) is available.
   *
   * Behaviour
   *  - If cached and fresh (TTL) and not forced, returns cached entry.
   *  - Otherwise fetches newest timestamp/name from the regional backend
   *    (deduped per uid),
   *    updates caches, and returns the CachedProfileInfo entry.
   *
   * @param {{ uid: string, ttlMs?: number, force?: boolean }} args
   * @returns {Promise<CachedProfileInfo>}
   */
  const ensureProfileInfo = useCallback(
    async ({ uid, ttlMs = DEFAULT_TTL_MS, force = false }) => {
      if (!uid) throw new Error("ensureProfileInfo: uid is required");

      const existing = getCachedProfileInfo(uid);
      if (
        !force &&
        ((existing?.status === "ready" && isEntryFresh(existing, ttlMs)) ||
          (existing?.status === "error" &&
            isEntryFresh(existing, ERROR_RETRY_TTL_MS)))
      ) {
        return existing;
      }

      if (inflightInfoRef.current[uid]) return inflightInfoRef.current[uid];

      // best-effort loading marker
      setInfoByUid((prev) => ({
        ...prev,
        [uid]: {
          status: "loading",
          latestMs: prev[uid]?.latestMs ?? null,
          newestName: prev[uid]?.newestName ?? null,
          error: null,
          fetchedAtMs: prev[uid]?.fetchedAtMs ?? null,
        },
      }));

      inflightInfoRef.current[uid] = (async () => {
        try {
          const info = await fetchLatestMetadataInfo(uid); // {latestMs, newestName} | null

          /** @type {CachedProfileInfo} */
          const entry = {
            status: "ready",
            latestMs: info?.latestMs ?? null,
            newestName: info?.newestName ?? null,
            error: null,
            fetchedAtMs: Date.now(),
          };

          setInfoByUid((prev) => ({ ...prev, [uid]: entry }));
          writeSessionInfo(uid, entry);
          return entry;
        } catch (e) {
          const msg = e?.message ? String(e.message) : "Unknown error";

          /** @type {CachedProfileInfo} */
          const entry = {
            status: "error",
            latestMs: null,
            newestName: null,
            error: msg,
            fetchedAtMs: Date.now(),
          };

          setInfoByUid((prev) => ({ ...prev, [uid]: entry }));
          writeSessionInfo(uid, entry);
          return entry;
        } finally {
          delete inflightInfoRef.current[uid];
        }
      })();

      return inflightInfoRef.current[uid];
    },
    [getCachedProfileInfo, writeSessionInfo],
  );

  /* ------------------------------------------------------------------ */
  /* context value                                                       */
  /* ------------------------------------------------------------------ */

  const value = useMemo(
    () => ({
      // profile JSON
      getCachedProfile,
      ensureProfile,
      setProfile,

      // profile info (timestamp)
      getCachedProfileInfo,
      ensureProfileInfo,
      setProfileInfo,

      // hygiene
      clearProfile,
      clearAllProfiles,
    }),
    [
      getCachedProfile,
      ensureProfile,
      setProfile,
      getCachedProfileInfo,
      ensureProfileInfo,
      setProfileInfo,
      clearProfile,
      clearAllProfiles,
    ],
  );

  return (
    <ProfilePrefetchContext.Provider value={value}>
      {children}
    </ProfilePrefetchContext.Provider>
  );
}

/**
 * Hook to consume the profile prefetch context.
 *
 * @returns {{
 *   getCachedProfile: (uid: string) => (CachedProfile|null),
 *   ensureProfile: (args: { uid: string, ttlMs?: number, force?: boolean }) => Promise<object|null>,
 *   setProfile: (uid: string, data: object|null) => void,
 *   getCachedProfileInfo: (uid: string) => (CachedProfileInfo|null),
 *   ensureProfileInfo: (args: { uid: string, ttlMs?: number, force?: boolean }) => Promise<CachedProfileInfo>,
 *   setProfileInfo: (uid: string, info: { latestMs?: number|null, newestName?: string|null }) => void,
 *   clearProfile: (uid: string) => void,
 *   clearAllProfiles: () => void,
 * }}
 */
export function useProfilePrefetch() {
  const ctx = useContext(ProfilePrefetchContext);
  if (!ctx) {
    throw new Error(
      "useProfilePrefetch must be used within a ProfilePrefetchProvider",
    );
  }
  return ctx;
}
